import { Link, useLocation } from "react-router-dom";
import "./header.css";
import { useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "./UserContext";
import { StreamChatContext } from "./StreamChatProvider";
import Cookies from "js-cookie";
import AuthModal from "./AuthModal";
import MobileMenu from "./MobileMenu";
import logo from "../assets/logo/logo.png";

export default function Header() {
  const { user, setUser, setProfileFetched, stripeStatus } =
    useContext(UserContext);
  const { unreadCount } = useContext(StreamChatContext);
  // Check if Stripe setup is incomplete for hosts
  const stripeIncomplete =
    user?.isHost && stripeStatus && !stripeStatus.isComplete;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openAuthModal = () => {
    setIsAuthModalOpen(true);
    setIsDropdownOpen(false);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  async function logout() {
    Cookies.remove("authToken"); // Remove the token from cookies
    setUser(null);
    setProfileFetched(false); // Reset profile fetched state
  }

  useEffect(() => {
    console.log(user);
  }, [user]);

  return (
    <div className="border-b border-gray-100 bg-[var(--secondary)]">
      {/* Mobile header */}
      <MobileMenu onOpenAuthModal={openAuthModal} />

      {/* Desktop header */}
      <div className="hidden md:block container mx-auto">
        <header className="container flex justify-between items-center py-2 mb-4">
          <div className="flex items-center gap-8">
            <Link to={"/"} className="">
              <img src={logo} alt="AirArrange Logo" className="w-60 h-13" />
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/workspaces"
                className="flex text-white items-center gap-2 text-sm font-medium relative group rounded-full px-4 py-2 transition-all"
              >
                {location.pathname === "/workspaces" && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
                Workspaces
                <span className="absolute  bottom-0 left-1/2 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
              </Link>
              <Link
                to="/aboutUs"
                className="flex text-white items-center gap-2 text-sm font-medium relative group rounded-full px-4 py-2 transition-all"
              >
                {location.pathname === "/aboutUs" && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
                About Us
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
              </Link>
              <Link
                to="/solutions"
                className="flex text-white items-center gap-2 text-sm font-medium relative group rounded-full px-4 py-2 transition-all"
              >
                {location.pathname === "/solutions" && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
                Solutions
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full group-hover:left-0"></span>
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Link
                to={location.pathname === "/host" ? "/account" : "/host"}
                className="hidden hover:drop-shadow-xl hover:bg-[var(--primary)] hover:text-black
                text-white rounded-2xl p-4 md:flex items-center text-sm
                font-medium relative group transition-all"
              >
                {location.pathname === "/host" ? "Switch to experience" : "Switch to Hosting"}
              </Link>
            ) : (
              <button
                onClick={openAuthModal}
                className="hidden hover:drop-shadow-xl hover:bg-[var(--primary)] hover:text-black
                text-white rounded-2xl p-4 md:flex items-center text-sm
                font-medium relative group transition-all"
              >
                List your Workspace
              </button>
            )}
            <div className="relative" ref={dropdownRef}>
              <button
                id="user-menu-button"
                onClick={toggleDropdown}
                className="relative flex items-center border bg-white border-gray-300 rounded-full py-2 px-4 gap-2 hover:shadow-md hover:bg-gray-200 transition-shadow"
              >
                {user ? (
                  <div className="hidden sm:block">{user?.name?.[0]}</div>
                ) : (
                  <div className="hidden sm:block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 640"
                      width={20}
                      height={20}
                      aria-hidden="true"
                    >
                      <path d="M96 160C96 142.3 110.3 128 128 128L512 128C529.7 128 544 142.3 544 160C544 177.7 529.7 192 512 192L128 192C110.3 192 96 177.7 96 160zM96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320zM544 480C544 497.7 529.7 512 512 512L128 512C110.3 512 96 497.7 96 480C96 462.3 110.3 448 128 448L512 448C529.7 448 544 462.3 544 480z" />
                    </svg>
                  </div>
                )}
                {/* Unread message notification dot */}
                {user && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex items-center justify-center rounded-full h-5 w-5 bg-red-500 text-white text-xs font-bold">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  </span>
                )}
                {/* Stripe incomplete notification dot */}
                {stripeIncomplete && unreadCount === 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                )}
              </button>

              {/* Dropdown menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-2xl z-50 border border-gray-100">
                  {user ? (
                    <>
                      {user.isHost ? (
                        <div>
                          <Link
                            to="/helpCentre"
                            className="flex flex-row gap-1 block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors font-medium"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="1.5"
                              stroke="currentColor"
                              className="size-6"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
                              />
                            </svg>
                            Help Centre
                          </Link>
                          <div className="border-t border-gray-200 my-1"></div>
                          <Link
                            to="/account?section=profile"
                            className="relative flex items-center justify-between px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors font-medium"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Account
                            {stripeIncomplete && (
                              <span className="flex h-3 w-3 items-center justify-center rounded-full bg-red-500"></span>
                            )}
                          </Link>
                          <Link
                            to="/host?section=bookings"
                            className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            My Bookings
                          </Link>
                          <Link
                            to="/host?section=listings"
                            className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            My Workspaces
                          </Link>
                          <Link
                            to="/messages"
                            className="relative flex items-center justify-between px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Messages
                            {unreadCount > 0 && (
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
                                {unreadCount > 9 ? "9+" : unreadCount}
                              </span>
                            )}
                          </Link>
                          <div className="border-t border-gray-200 my-1"></div>
                          <Link
                            to="/contactUs"
                            className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Contact us
                          </Link>
                        </div>
                      ) : (
                        <div>
                          <Link
                            to="/helpCentre"
                            className="flex flex-row gap-1 block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors font-medium"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="1.5"
                              stroke="currentColor"
                              className="size-6"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
                              />
                            </svg>
                            Help Centre
                          </Link>
                          <div className="border-t border-gray-200 my-1"></div>
                          <Link
                            to="/account?section=profile"
                            className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors font-medium"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Account
                          </Link>
                          <Link
                            to="/account?section=upcomingBookings"
                            className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            My Bookings
                          </Link>
                          <Link
                            to="/account?section=pastBookings"
                            className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Past Bookings
                          </Link>
                          <Link
                            to="/messages"
                            className="relative flex items-center justify-between px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Messages
                            {unreadCount > 0 && (
                              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
                                {unreadCount > 9 ? "9+" : unreadCount}
                              </span>
                            )}
                          </Link>
                          <div className="border-t border-gray-200 my-1"></div>
                          <Link
                            to="/contactUs"
                            className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Contact us
                          </Link>
                        </div>
                      )}
                      <div className="border-t border-gray-200 my-1"></div>
                      <button
                        onClick={() => {
                          logout();
                          setIsDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                      >
                        Log Out
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={openAuthModal}
                        className="w-full text-left px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors font-medium"
                      >
                        Log in or sign up
                      </button>
                      <div className="border-t border-gray-200 my-1"></div>
                      {/* <Link 
                                            to="/refer" 
                                            className="block px-4 py-3 hover:bg-gray-100 transition-colors"
                                            onClick={() => setIsDropdownOpen(false)}
                                        >
                                            Refer someone
                                        </Link> */}
                      <Link
                        to="/contact"
                        className="block px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Contact us
                      </Link>
                      <div className="border-t border-gray-200 my-1"></div>
                      <Link
                        to="/help"
                        className="px-4 py-3 hover:bg-[var(--secondary)] hover:text-white rounded-2xl transition-colors flex flex-row gap-1"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
                          />
                        </svg>
                        Help center
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
      </div>

      {/* Login Modal */}
      {isAuthModalOpen && (
        <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
      )}
    </div>
  );
}
